import React, { useEffect } from "react";
import { EventAdd } from "./EventAdd";
import { EventDetails } from "./EventDetails";
import { EventRemove } from "./EventRemove";
import { EventUpdate } from "./EventUpdate";
import { useEventCalendar, useEventCalendarState } from "../../../hooks";
import { CalendarConfig } from "./CalendarConfig";
import { DatePickerModal } from "../../ui/DatePickerModal";

type Props = {
  identityIds?: string[];
  initialDate?: Date;
  newEventAllowed?: boolean;
  updateEventAllowed?: boolean;
  setView?: string;
  isTask?: boolean;
  taskId?: string;
  visable?: boolean;
  setColors?: any;
};

export interface IEvent {
  id: string;
  groupId: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  color?: string;
  textColor?: string;
  editable: boolean;
  taskId?: string;
  description?: string;
  appointmentTypeId?: string;
  identityId?: string;
  identityName?: string;
  associatedIds?: string[];
}

const EventCalendar: React.FC<Props> = ({
  identityIds = [],
  initialDate = new Date(),
  newEventAllowed = false,
  updateEventAllowed = false,
  setView = "",
  isTask = false,
  taskId,
  visable,
  setColors
}) => {
  const { calendarRef, events, updateEvents, handleEventChange } = useEventCalendar(
    identityIds,
    updateEventAllowed,
    setColors
  );

  const {
    showDatePicker,
    setShowDatePicker,
    showEventDetails,
    setShowEventDetails,
    showEventAdd,
    setShowEventAdd,
    showEventUpdate,
    setShowEventUpdate,
    showEventRemove,
    setShowEventRemove,
    eventAddStart,
    eventAddEnd,
    eventAddIsAllDay,
    eventToBeUpdated,
    setEventToBeUpdated,
    currentUser,
    handleEventClick,
    handleDatePickerSubmit,
    handleSelect,
    handleAddEventButton,
    handleGoToDateButton,
  } = useEventCalendarState(visable);

  const shouldShowAddEventButton = newEventAllowed;

  useEffect(() => {
    if (visable && calendarRef.current) {
      calendarRef.current.getApi().updateSize();
    }
  }, [visable, calendarRef]);

  const onEventClick = (info: any) => handleEventClick(info, calendarRef);
  const onSelect = (selectionInfo: any) => handleSelect(selectionInfo);
  const onDatesSet = (info: any) => updateEvents(info.start, info.end);
  const onDatePickerSubmit = (values: any, formikHelpers: any) => 
    handleDatePickerSubmit(values, formikHelpers, calendarRef);

  return (
    <>
      <CalendarConfig
        calendarRef={calendarRef}
        events={events}
        initialView={setView === "" ? "dayGridMonth" : setView}
        initialDate={initialDate}
        updateEventAllowed={updateEventAllowed}
        newEventAllowed={newEventAllowed}
        shouldShowAddEventButton={shouldShowAddEventButton}
        onEventClick={onEventClick}
        onSelect={onSelect}
        onEventChange={handleEventChange}
        onDatesSet={onDatesSet}
        onGoToDateButton={handleGoToDateButton}
        onAddEventButton={handleAddEventButton}
        updateEvents={updateEvents}
      />
      
      <EventDetails
        show={showEventDetails}
        onClose={(update) => {
          if (update) {
            setShowEventUpdate(true);
          }
          setShowEventDetails(false);
        }}
        event={eventToBeUpdated}
        updateEventAllowed={updateEventAllowed}
      />
      
      <EventAdd
        show={showEventAdd}
        onClose={(added) => {
          if (added) {
            if (calendarRef.current) {
              updateEvents();
            }
          }
          setShowEventAdd(false);
        }}
        dateTimeStart={eventAddStart}
        dateTimeEnd={eventAddEnd}
        isAllDay={eventAddIsAllDay}
        identityIds={identityIds}
        isTaskEvent={isTask}
        taskId={taskId}
      />
      
      <EventUpdate
        show={showEventUpdate}
        onClose={(updated, removed) => {
          if (removed) {
            setShowEventRemove(true);
          }
          if (updated) {
            if (calendarRef.current) {
              updateEvents();
            }
          }
          setShowEventUpdate(false);
        }}
        eventToBeUpdated={eventToBeUpdated}
        identityIds={eventToBeUpdated?.associatedIds}
      />
      
      <EventRemove
        show={showEventRemove}
        onClose={(removed) => {
          if (removed) {
            if (calendarRef.current) {
              updateEvents();
            }
          }
          setShowEventRemove(false);
        }}
        eventToBeUpdated={eventToBeUpdated}
      />
      
      <DatePickerModal
        show={showDatePicker}
        onHide={() => setShowDatePicker(false)}
        onSubmit={onDatePickerSubmit}
        currentDate={calendarRef.current ? calendarRef.current.getApi().getDate() : new Date()}
      />
    </>
  );
};

export { EventCalendar };
